import { ComponentStore } from '@ngrx/component-store';
import { inject, Injectable } from '@angular/core';
import { catchError, concatMap } from 'rxjs/operators';
import { EMPTY, Observable, of } from 'rxjs';
import equal from 'fast-deep-equal';
import { IGcComponentStore } from '@app/core/interfaces/gc-component-store.interface';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { Store } from '@ngrx/store';
import { selectMainProjectId } from '@app/main/main-screen/store/main-selection/main-shared-selection/main-shared-selection.selectors';
import { CorrelationsApiService } from '@app/api/services/correlations-api.service';
import { getCorrelation } from '@app/main/main-screen/store/data/correlations/correlations.actions';
import { IFpCorrelationData } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/interfaces/fp-correlation-data';
import {
  FpCorrelationCommentsActionGetData,
  FpCorrelationCommentsActionGetDataSuccesses,
  FpCorrelationCommentsActions,
  FpCorrelationCommentsActionType,
  FpCorrelationCommentsActionUpdateComment,
  FpCorrelationCommentsActionUpdateCommentError,
  FpCorrelationCommentsActionUpdateCommentSuccess,
} from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-comments/services/fp-correlation-comments/fp-correlation-comments.actions';
import {
  IFpCorrelationCommentsState,
  initialFpCorrelationCommentsState,
} from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-comments/services/fp-correlation-comments/fp-correlation-comments.state';

@Injectable()
export class FpCorrelationCommentsStore
  extends ComponentStore<IFpCorrelationCommentsState>
  implements IGcComponentStore<FpCorrelationCommentsActions>
{
  public readonly data = inject<IFpCorrelationData>(FLOATING_POPUP_DATA);
  protected readonly store = inject(Store);
  protected readonly correlationsApiService = inject(CorrelationsApiService);

  private readonly mainProjectId = this.store.selectSignal(selectMainProjectId);

  constructor() {
    super(initialFpCorrelationCommentsState);
  }

  public readonly date$ = this.select(s => s.date, { equal });
  public readonly data$ = this.select(s => s.data, { equal });
  public readonly loading$ = this.select(s => s.loading, { equal });
  public readonly saved$ = this.select(s => s.saved, { equal });
  public readonly error$ = this.select(s => s.error, { equal });
  public readonly comment$ = this.select(s => s.data?.correlation?.comment ?? null, { equal });
  public readonly commentContent$ = this.select(s => s.data?.correlation?.comment?.content ?? '', { equal });

  public dispatch = this.effect((action$: Observable<FpCorrelationCommentsActions>) =>
    action$.pipe(
      concatMap(action => {
        switch (action.type) {
          case FpCorrelationCommentsActionType.GetData: {
            this.reducerGetData();
            this.effectGetData(action);
            break;
          }
          case FpCorrelationCommentsActionType.GetDataSuccesses: {
            this.reducerGetDataSuccesses(action);
            break;
          }
          case FpCorrelationCommentsActionType.UpdateComment: {
            this.reducerUpdateComment();
            this.effectUpdateComment(action);
            break;
          }
          case FpCorrelationCommentsActionType.UpdateCommentSuccess: {
            this.reducerUpdateCommentSuccess(action);
            break;
          }
          case FpCorrelationCommentsActionType.UpdateCommentError: {
            this.reducerUpdateCommentError(action);
            break;
          }
        }
        return of(action);
      })
    )
  );

  private reducerGetData() {
    this.patchState({
      loading: true,
    });
  }

  private reducerGetDataSuccesses(action: FpCorrelationCommentsActionGetDataSuccesses) {
    this.patchState({
      date: action.date,
      data: action.data,
      loading: false,
    });
  }

  private reducerUpdateComment() {
    this.patchState({
      loading: true,
      saved: false,
      error: null,
    });
  }

  private reducerUpdateCommentSuccess(action: FpCorrelationCommentsActionUpdateCommentSuccess) {
    const state = this.get();

    if (!state.data) {
      this.patchState({ loading: false });
      return;
    }

    this.patchState({
      loading: false,
      saved: true,
      error: null,
      data: {
        ...state.data,
        correlation: {
          ...state.data.correlation,
          comment: action.comment,
        },
      },
    });

    this.store.dispatch(getCorrelation({ correlationId: state.data.correlation.id }));
  }

  private reducerUpdateCommentError(action: FpCorrelationCommentsActionUpdateCommentError) {
    this.patchState({
      loading: false,
      saved: false,
      error: action.error,
    });
  }

  private effectGetData(action: FpCorrelationCommentsActionGetData) {
    this.dispatch({
      type: FpCorrelationCommentsActionType.GetDataSuccesses,
      date: action.date,
      data: action.popupMainData,
    });
  }

  private effectUpdateComment(action: FpCorrelationCommentsActionUpdateComment) {
    const state = this.get();
    const projectId = this.mainProjectId();
    const correlation = state.data?.correlation;
    const mode = state.data?.correlationMode;

    if (!projectId || !correlation || !mode) {
      this.patchState({ loading: false });

      return;
    }

    this.correlationsApiService
      .updateComment(mode, projectId, correlation.id, { content: action.content })
      .pipe(
        catchError(error => {
          this.dispatch({
            type: FpCorrelationCommentsActionType.UpdateCommentError,
            error,
          });

          return EMPTY;
        })
      )
      .subscribe(response => {
        this.dispatch({
          type: FpCorrelationCommentsActionType.UpdateCommentSuccess,
          comment: response.data,
        });
      });
  }
}
